// Web version of Stripe service using Stripe.js
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG } from '../config/stripe';

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
  }
  return stripePromise;
};

// Get API Gateway base URL
const getApiBaseUrl = () => {
  return process.env.REACT_APP_STRIPE_API_URL || 'https://your-api-gateway-url.execute-api.us-east-1.amazonaws.com/dev';
};

// Initialize Stripe (web doesn't need special init)
export const initializeStripe = async () => {
  if (!STRIPE_CONFIG.publishableKey) {
    console.warn('Stripe publishable key not configured');
    return false;
  }
  try {
    await getStripe();
    return true;
  } catch (error) {
    console.error('Error initializing Stripe:', error);
    return false;
  }
};

/**
 * Create a Stripe Connect account for a club
 */
export const createConnectAccount = async (clubId, email, clubName) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/create-account`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clubId,
          email,
          clubName,
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create Stripe account');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating Connect account:', error);
    throw error;
  }
};

/**
 * Get account link for onboarding or returning to dashboard
 */
export const getAccountLink = async (accountId, type = 'account_onboarding') => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/account-link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountId,
          type,
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to get account link');
    }
    
    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Error getting account link:', error);
    throw error;
  }
};

/**
 * Create payment intent for an event RSVP
 */
export const createEventPaymentIntent = async (eventId, userId, amount, clubStripeAccountId, platformFeePercentage = STRIPE_CONFIG.platformFeePercentage) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/create-payment-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId,
          userId,
          amount,
          clubStripeAccountId,
          platformFeePercentage,
          type: 'event',
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create payment intent');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Create payment intent for a tournament entry
 */
export const createTournamentPaymentIntent = async (tournamentId, userId, amount, clubStripeAccountId, platformFeePercentage = STRIPE_CONFIG.platformFeePercentage) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/create-payment-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tournamentId,
          userId,
          amount,
          clubStripeAccountId,
          platformFeePercentage,
          type: 'tournament',
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create payment intent');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Process payment for an event using Stripe Payment Element
 * This uses the Payment Element which supports multiple payment methods
 */
export const processEventPayment = async (event, userId, paymentIntentClientSecret) => {
  try {
    const stripe = await getStripe();
    
    if (!stripe) {
      throw new Error('Stripe not initialized');
    }

    // Use Stripe.js confirmPayment with Payment Element
    // For Payment Element, we use confirmPayment which handles 3D Secure automatically
    const { error, paymentIntent } = await stripe.confirmPayment({
      clientSecret: paymentIntentClientSecret,
      confirmParams: {
        return_url: window.location.origin + window.location.pathname,
      },
      redirect: 'if_required', // Only redirect if 3D Secure is required
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return { success: true, paymentIntent };
  } catch (error) {
    console.error('Payment error:', error);
    throw error;
  }
};

/**
 * Process payment for a tournament entry
 */
export const processTournamentPayment = async (tournament, userId, paymentIntentClientSecret) => {
  try {
    const stripe = await getStripe();
    
    if (!stripe) {
      throw new Error('Stripe not initialized');
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(paymentIntentClientSecret);
    
    if (error) {
      throw new Error(error.message);
    }
    
    return { success: true, paymentIntent };
  } catch (error) {
    console.error('Payment error:', error);
    throw error;
  }
};

/**
 * Confirm payment for an event (alternative method)
 */
export const confirmEventPayment = async (paymentIntentId, paymentMethodId) => {
  try {
    const stripe = await getStripe();
    
    const { error, paymentIntent } = await stripe.confirmCardPayment(paymentIntentId, {
      payment_method: paymentMethodId,
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return { success: true, paymentIntent };
  } catch (error) {
    console.error('Payment confirmation error:', error);
    throw error;
  }
};

/**
 * Check if a club's Stripe account is ready to accept payments
 */
export const checkClubStripeStatus = async (clubId) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/stripe/check-account-status`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clubId }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to check Stripe account status');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking Stripe status:', error);
    throw error;
  }
};

